import {
    NativeRequestPayload,
    NativeRequestType,
    NativeResponsePayload,
    NativeResponseType,
    NativeRequest,
    LogClevertapEventRequestPayload,
} from "@swiggy-private/native-bridge";
import { BridgeHandler, STATUS_CODES } from "@swiggy-private/web-bridge-sdk";
import { isEmpty, safeJsonParse } from "@swiggy-private/js-utils";
import {
    isIxigoUpiBridgeAvailable,
    ixigoExpandToAllFormats,
    ixigoResolveToPackageName,
} from "@checkout-core/helpers/Common";
import { handleLoginFlowNavigation } from "@checkout-core/helpers/LoginFlowNavigation";
import { MWEB_UPI_REDIRECT_STATUS } from "@checkout-core/constants/MwebUpiAppWhitelist";
import { GlobalHeaderState } from "./Headers";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
import MoengageWebSdk from "@moengage/web-sdk";
import { SOURCE_CHANNEL } from "../constants";
import { getPreCartLinkFromStorageDWeb } from "@checkout-core/helpers/Storage";
import { isDWebPlatform } from "@checkout-core/helpers/Common";

export const customHandlers: Partial<
    Record<NativeRequestType, BridgeHandler<NativeRequestPayload, NativeResponsePayload>>
> = {
    [NativeRequestType.CLOSE_WEBVIEW]: {
        handle: async () => {
            /*
             * TODO: Add DO BL check
             * This component requires a custom back handling for the special usecase through which it is rendered.
             * We are using redirection logic to load the DO cart on web, and the usual browser provided convention
             * of using histoy.back() does not work here. This is because of the custom MiniRouter than CW uses,
             * it pushes a state manually once it is loaded, so during redirection from caller web-client e.g, SEO,
             * browser pushes state first to the history stack and CW also pushes a state in history stack,
             * which causes problem with back handling. To revert its effect, we are doing history.go(-2) to undo
             * 2 states instead of just 1 through history.back()
             */

            // Redirect to menu page if coming from login flow (for all flows)
            const navigationResponse = await handleLoginFlowNavigation();
            if (navigationResponse) {
                return navigationResponse;
            }
            const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";

            const isDweb = isDWebPlatform(platform);
            const preCartLink = getPreCartLinkFromStorageDWeb();

            if (isDweb && !isEmpty(preCartLink)) {
                window.location.replace(preCartLink);
            } else {
                // Default behavior: go back 2 steps in history
                history.go(-2);
            }

            return { statusCode: 0, type: NativeResponseType.SUCCESS, requestId: "" };
        },
    },

    /**
     * Ixigo UPI Intent: Get available UPI apps
     * When running in Ixigo webview, fetch UPI apps from native bridge.
     * Otherwise return empty — mweb/dweb availability is handled via GPO in useUpiMethodsAvailable.
     */
    [NativeRequestType.IS_UPI_INTENT_METHOD_AVAILABLE]: {
        handle: async (): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            // If Ixigo bridge is available, use it exclusively
            if (isIxigoUpiBridgeAvailable() && window.ixigoBridge?.getUPIAppsList) {
                try {
                    const ixigoAppsRaw = await window.ixigoBridge.getUPIAppsList();
                    const ixigoApps: Array<{ package: string }> =
                        typeof ixigoAppsRaw === "string" ? (safeJsonParse(ixigoAppsRaw, []) ?? []) : ixigoAppsRaw;
                    const methods = ixigoApps.flatMap((app) => ixigoExpandToAllFormats(app.package));
                    return {
                        payload: { methods } as NativeResponsePayload,
                        statusCode: STATUS_CODES.SUCCESS_CODE,
                    };
                } catch {
                    // Ixigo bridge failed - return empty methods (no fallback to mweb)
                    return {
                        payload: { methods: [] } as NativeResponsePayload,
                        statusCode: STATUS_CODES.ERROR,
                    };
                }
            }

            return {
                payload: { methods: [] } as NativeResponsePayload,
                statusCode: STATUS_CODES.SUCCESS_CODE,
            };
        },
    },

    /**
     * Ixigo UPI Intent: open UPI app via partner bridge.
     * mweb/dweb pay uses handleMwebUpiIntentRedirect in payment-client OrderHelper (not this handler).
     */
    [NativeRequestType.PAY_USING_UPI_INTENT]: {
        handle: async (
            req: NativeRequest<NativeRequestPayload>,
        ): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            const payload = req.payload as {
                upiParams: { orderId: string; url: string; methodName: string };
            };
            const { orderId, url, methodName } = payload.upiParams;

            if (isIxigoUpiBridgeAvailable() && window.ixigoBridge?.openUPIApp) {
                try {
                    const packageName = ixigoResolveToPackageName(methodName);
                    await window.ixigoBridge.openUPIApp(packageName, url);
                    return {
                        payload: { status: MWEB_UPI_REDIRECT_STATUS.SUCCESS, orderId } as NativeResponsePayload,
                        statusCode: STATUS_CODES.SUCCESS_CODE,
                    };
                } catch {
                    return {
                        payload: { status: MWEB_UPI_REDIRECT_STATUS.FAILURE } as NativeResponsePayload,
                        statusCode: STATUS_CODES.ERROR,
                    };
                }
            }

            return {
                payload: { status: MWEB_UPI_REDIRECT_STATUS.FAILURE } as NativeResponsePayload,
                statusCode: STATUS_CODES.ERROR,
            };
        },
    },
    [NativeRequestType.LOG_CLEVERTAP_EVENT]: {
        handle: async (
            req: NativeRequest<NativeRequestPayload>,
        ): Promise<{ payload?: NativeResponsePayload; statusCode?: number }> => {
            const channel = GlobalHeaderState.getHeader(SwgyHeaderKeysList.xChannel) || "";
            if (channel !== SOURCE_CHANNEL.JIO_HOTSTAR) {
                return {
                    payload: {},
                    statusCode: STATUS_CODES.SUCCESS_CODE,
                };
            }
            try {
                const payload = req.payload as LogClevertapEventRequestPayload;
                if (payload.eventName) {
                    const triggerEvent = async () => {
                        return MoengageWebSdk.trackEvent(payload.eventName, {
                            ...payload.params,
                            channel,
                        });
                    };
                    if (!MoengageWebSdk.isSdkEnabled()) {
                        await MoengageWebSdk.enableSdk();
                        await triggerEvent();
                    } else {
                        await triggerEvent();
                    }
                }
                return {
                    payload: {},
                    statusCode: STATUS_CODES.SUCCESS_CODE,
                };
            } catch (error) {
                return {
                    payload: {},
                    statusCode: STATUS_CODES.ERROR,
                };
            }
        },
    },
};
